import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, Loader } from 'semantic-ui-react'
import ClimaProgramQuery from '~/QueryHooks/ClimaProgramQuery'
import Dialog from '~/components/Dialog'
import {
  FieldsGrid,
  FieldWrapper,
  FieldLabel,
  FieldValue,
  SectionTitle
} from './style'
import { Nullable } from '~/types'
import { formatNumber, handleErrors } from '~/utils/helpers'
import { ClimaProgramService } from '~/api/services/climaProgram'

interface RegistrationViewModalProps {
  registrationId: Nullable<string>
  onClose: () => void
}

const RegistrationViewModal: React.FC<RegistrationViewModalProps> = ({ registrationId, onClose }) => {
  const { t } = useTranslation()
  const [isLoadingFile, setIsLoadingFile] = useState(false)

  const {
    data: registrationData,
    isLoading: isLoadingRegData,
    refetch: fetchRegistrationData,
    isFetching: isFetchingRegistrationData
  } = ClimaProgramQuery.showRegistration(registrationId)

  useEffect(() => {
    if (registrationId) {
      fetchRegistrationData()
    }
  }, [registrationId])

  function formatDate(date: Date | string | null) {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('de-CH')
  }

  function formatBoolean(value: number | null) {
    if (value === null || value === undefined) return '-'
    return value === 1 ? t('Yes') : t('No')
  }

  function renderField(icon: string, label: string, value: string | number | null | undefined) {
    return (
      <FieldWrapper>
        <FieldLabel>
          <Icon name={icon as any} />
          {label}
        </FieldLabel>
        <FieldValue>{value ?? '-'}</FieldValue>
      </FieldWrapper>
    )
  }

  async function handleDownloadFile() {
    setIsLoadingFile(true)
    try {
      const response = await ClimaProgramService.getRegistrationFile(registrationId)
      const link = document.createElement('a')
      link.href = response.data
      link.download = registrationData?.feedingPlanOriginalFilename || ''
      link.click()
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingFile(false)
  }

  function renderDownloadField(icon: string, label: string, value: string | number | null | undefined) {
    return (
      <FieldWrapper>
        <FieldLabel>
          <Icon name='file' />
          {label}
        </FieldLabel>
        <FieldValue
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => !isLoadingFile && handleDownloadFile()}
        >
          {isLoadingFile ? (
            <Loader inverted active inline size='small' />
          ) : (
            <Icon name={icon as any} />
          )}
          <div>{value}</div>
        </FieldValue>
      </FieldWrapper>
    )
  }

  function renderContent() {
    if (!registrationData) {
      return <p>{t('No registrationData? data available')}</p>
    }

    return (
      <>
        <SectionTitle as='h3'>{t('Company Information')}</SectionTitle>
        <FieldsGrid>
          {renderField('building', t('Company Name'), registrationData?.companyName)}
          {renderField('id card', t('AGIS ID'), registrationData?.agisId)}
          {renderField('calendar', t('Year of participation'), registrationData?.year)}
        </FieldsGrid>

        <SectionTitle as='h3'>{t('Address')}</SectionTitle>
        <FieldsGrid>
          {renderField('road', t('Street'), `${registrationData?.streetName} ${registrationData?.streetNumber}`)}
          {renderField('map pin', t('ZIP'), registrationData?.zip)}
          {renderField('marker', t('City'), registrationData?.city)}
        </FieldsGrid>

        <SectionTitle as='h3'>{t('Farm Details')}</SectionTitle>
        <FieldsGrid>
          {renderField('paw', t('Number of Cows'), formatNumber(registrationData?.cowsNumber, 0))}
          {renderField('tint', t('Average milk yield per cow per year (in kg)'), `${formatNumber(registrationData?.avgMilkAmount, 0)} kg`)}
          {renderField('leaf', t('Annual amount of concentrated feed per cow (in kg)'), `${formatNumber(registrationData?.yearlyConcentratedFeedAmountPerCow)} kg`)}
        </FieldsGrid>

        <SectionTitle as='h3'>{t('Additional Information')}</SectionTitle>
        <FieldsGrid>
          {renderField('history', t('Changes in Last Three Years'), formatBoolean(registrationData?.changesInLastThreeYears))}
          {renderField('calculator', t('Self Calculated Feeding Plan'), formatBoolean(registrationData?.selfCalculatedFeedingPlan))}
        </FieldsGrid>

        <SectionTitle as='h3'>{t('Consultant Information')}</SectionTitle>
        <FieldsGrid>
          {renderField('user', t('First Name'), registrationData?.consultantFirstName)}
          {renderField('user', t('Last Name'), registrationData?.consultantLastName)}
          {renderField('phone', t('Phone'), registrationData?.consultantPhoneNumber)}
          {renderField('mail', t('Email'), registrationData?.consultantEmail)}
          {renderField('building', t('Company'), registrationData?.consultantCompanyName)}
        </FieldsGrid>

        {(registrationData?.feedingPlanOriginalFilename || registrationData?.feedingPlanFilename) && (
          <>
            <SectionTitle as='h3'>{t('Feeding Plan')}</SectionTitle>
            <FieldsGrid>
              {renderDownloadField('download', t('File'), registrationData?.feedingPlanOriginalFilename || registrationData?.feedingPlanFilename)}
            </FieldsGrid>
          </>
        )}

        <SectionTitle as='h3'>{t('Timestamps')}</SectionTitle>
        <FieldsGrid>
          {renderField('clock', t('Created At'), formatDate(registrationData?.createdAt))}
          {renderField('clock outline', t('Updated At'), formatDate(registrationData?.updatedAt))}
        </FieldsGrid>
      </>
    )
  }

  return (
    <Dialog
      type='dialog'
      size='large'
      textHeader={t('Registration details for the climate program')}
      isDialogOpen={Boolean(registrationId)}
      isLoading={isLoadingRegData || isFetchingRegistrationData}
      textClose={t('Close')}
      handleClose={onClose}
      textContent={renderContent()}
    />
  )
}

export default RegistrationViewModal
